import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { EApplicationType } from "../../../../shared/oversight-core/enums/application-type";
import { EUtilityOptionType } from "../../../../shared/oversight-core/enums/utility-option-type";
import AppSelect, {
  Option,
} from "../../../../shared/oversight-core/ui-elements/app-select/app-select";
import AppInput from "../../../../shared/oversight-core/ui-elements/input/app-input";
import ModalContainer, {
  ModelContainerProps,
} from "../../../../shared/oversight-core/ui-elements/modal-container/modal-container";

interface IProps extends ModelContainerProps {
  show: boolean;
  onSave: (
    applicationType: EApplicationType,
    utilityOption: EUtilityOptionType,
    numberOfUsers: number,
    demandPerUser: number,
  ) => void;
}

const applicationTypeOptions: Option[] = [
  {
    value: EApplicationType.RESIDENTIAL,
    label: "Residential",
  },
  {
    value: EApplicationType.INSTITUTIONS,
    label: "Institutions",
  },
  {
    value: EApplicationType.HOTELS,
    label: "Hotels",
  },
  {
    value: EApplicationType.HOSTELS,
    label: "Hostels",
  },
  {
    value: EApplicationType.SCHOOLS,
    label: "Schools",
  },
];

const utilityOptions: Option[] = [
  {
    value: EUtilityOptionType.SHOWER,
    label: "Shower",
  },
  {
    value: EUtilityOptionType.BATHTUB_JACUZZI,
    label: "Bathtub/Jacuzzi",
  },
];

interface IFormInput {
  numberOfUsers: number | undefined;
  demandPerUser: number | undefined;
}

const defaultFormValues: IFormInput = {
  numberOfUsers: undefined,
  demandPerUser: undefined,
};

const WaterHeaterSystemModal = (props: IProps) => {
  const { show, onSave, ...rest } = props;

  const [selectedApplicationType, setSelectedApplicationType] =
    useState<Option>({ ...applicationTypeOptions[0] });
  const [selectedInstitution, setSelectedInstitution] = useState<Option>({
    ...applicationTypeOptions[2],
  });
  const [selectedUtilityOption, setSelectedUtilityOption] = useState<Option>({
    ...utilityOptions[0],
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IFormInput>({
    defaultValues: { ...defaultFormValues },
  });

  useEffect(() => {
    if (show) {
      setSelectedApplicationType({ ...applicationTypeOptions[0] });
      setSelectedInstitution({ ...applicationTypeOptions[2] });
      setSelectedUtilityOption({ ...utilityOptions[0] });
      reset({ ...defaultFormValues });
    }
  }, [show]);

  const onSubmit = (data: IFormInput) => {
    if (data.numberOfUsers) {
      onSave(
        selectedApplicationType.value === EApplicationType.RESIDENTIAL
          ? (selectedApplicationType.value as EApplicationType)
          : (selectedInstitution.value as EApplicationType),
        selectedUtilityOption.value as EUtilityOptionType,
        data.numberOfUsers,
        data.demandPerUser ?? 0,
      );
    }
  };

  return (
    <ModalContainer
      {...rest}
      title="Add Water Heater System"
      show={show}
      size="modal-md"
      onConfirm={handleSubmit(onSubmit)}
    >
      <>
        <AppSelect
          label="Application Type"
          selectedValue={selectedApplicationType}
          options={[...applicationTypeOptions.slice(0, 2)]}
          onChangeOption={(selectOption) => {
            setSelectedApplicationType(selectOption);
          }}
          isRequired
        />
        {selectedApplicationType.value === EApplicationType.INSTITUTIONS && (
          <AppSelect
            className="mt-4"
            label="Institution"
            selectedValue={selectedInstitution}
            options={[...applicationTypeOptions.slice(2)]}
            onChangeOption={(selectOption) => {
              setSelectedInstitution(selectOption);
            }}
            isRequired
          />
        )}
        <AppSelect
          className="mt-4"
          label="Utility Option"
          selectedValue={selectedUtilityOption}
          options={[...utilityOptions]}
          onChangeOption={(selectOption) => {
            setSelectedUtilityOption(selectOption);
          }}
          isRequired
        />
        <AppInput
          className="mt-4"
          label="Number of Users"
          placeholder="Number of Users"
          type="number"
          name="numberOfUsers"
          register={register("numberOfUsers", {
            required: "Number of users is required.",
            min: {
              value: 1,
              message: "Value should be greater than 0",
            },
          })}
          onKeyDown={(e) => {
            if (e.key === "." || e.key === "," || e.key === "e") {
              e.preventDefault();
            }
          }}
          errors={errors}
        />
        <AppInput
          className="mt-4"
          label="Demand per User"
          placeholder="Demand per User"
          type="number"
          name="demandPerUser"
          register={register("demandPerUser", {
            min: {
              value: 1,
              message: "Value should be greater than 0",
            },
          })}
          onKeyDown={(e) => {
            if (e.key === "." || e.key === "," || e.key === "e") {
              e.preventDefault();
            }
          }}
          errors={errors}
          isRequired={false}
        />
      </>
    </ModalContainer>
  );
};

export default WaterHeaterSystemModal;
